import { State } from 'model/State';
import { getFromHamsClient } from 'util/mac/api/account/get';
import { fetchWithParamValidation } from 'util/fetchWithParamValidation';
import { selectSwitchedUser } from '../selectors';
import { deleteFromHamsClient } from '../../../../util/mac/api/account/delete';
import {
  postToHamsClient,
  postToHamsClientWithResponseHandlers,
} from '../../../../util/mac/api/account/post';
import { MACCreditCardDetails } from './storage';
import { CreateLicenseDetails } from '../models/product';

export const childrenOverviews = (accountId: string, state: State) =>
  fetchWithParamValidation(({ id }) => getFromHamsClient(`/${id}/childrenOverviews`, state), {
    id: accountId,
  });

export const overviewWithOrgChildren = (state: State) =>
  fetchWithParamValidation(() => getFromHamsClient('/overview/withOrgChildren', state), {});

export const trackedElsewhere = (accountId: string, state: State) =>
  fetchWithParamValidation(({ id }) => getFromHamsClient(`/${id}/trackedElsewhere`, state), {
    id: accountId,
  });

export const isOnBehalfOfEmail = (accountId: string, state: State) => {
  const email = selectSwitchedUser(state);

  return fetchWithParamValidation(
    ({ id, onBehalf }) =>
      getFromHamsClient(`/${id}/partnerProfile`, state, { onBehalfOfEmail: onBehalf }),
    { id: accountId, onBehalf: email },
  );
};

export const isSiteOnConcession = (accountId: number, state: State) =>
  fetchWithParamValidation(({ id }) => getFromHamsClient(`/${id}/isSiteOnConcession`, state), {
    id: accountId,
  });

export const isSiteApplicableForConcession = (accountId: string, state: State) =>
  fetchWithParamValidation(
    ({ id }) => getFromHamsClient(`/${id}/isSiteApplicableForConcession`, state),
    {
      id: accountId,
    },
  );

export const CMTSelfServeLink = (accountId: string, state: State) =>
  fetchWithParamValidation(({ id }) => getFromHamsClient(`/${id}/CMTSelfServeLink`, state), {
    id: accountId,
  });

export const archiveUnarchiveAccount = (accountId: number, isArchived: boolean, state: State) =>
  fetchWithParamValidation(
    ({ id }) => postToHamsClient(`/${id}/setArchivedStatus/${isArchived}`, state),
    {
      id: accountId,
      isArchived,
    },
  );

export const getAccountHistory = (
  accountId: string,
  responseOffset: number,
  responseMaxResults: number,
  state: State,
) =>
  fetchWithParamValidation(
    ({ id, offset, maxResults }) =>
      getFromHamsClient(`/${id}/periods?offset=${offset}&maxResults=${maxResults}`, state),
    {
      id: accountId,
      offset: responseOffset,
      maxResults: responseMaxResults,
    },
  );

export const addContact = (
  accountId: string,
  contactId: string,
  contactRelationshipType: string,
  state: State,
) =>
  fetchWithParamValidation(
    ({ id, newContactId, relationshipType }) =>
      postToHamsClient(
        `/${id}/addContact/${newContactId}/${relationshipType}?makePrimary=false`,
        state,
      ),
    {
      id: accountId,
      newContactId: contactId,
      relationshipType: contactRelationshipType,
    },
  );

export const addMainContact = (
  accountId: string,
  contactId: string,
  contactRelationshipType: string,
  state: State,
) =>
  fetchWithParamValidation(
    ({ id, currentContactId, relationshipType }) =>
      postToHamsClient(
        `/${id}/addMainContact/${currentContactId}/${relationshipType.toString().toUpperCase()}`,
        state,
      ),
    {
      id: accountId,
      currentContactId: contactId,
      relationshipType: contactRelationshipType,
    },
  );

export const makePrimary = (
  accountId: string,
  contactId: string,
  contactRelationshipType: string,
  state: State,
) =>
  fetchWithParamValidation(
    ({ id, currentContactId, relationshipType }) =>
      postToHamsClient(
        `/${id}/sendPromotionNotificationEmail/${currentContactId}/${relationshipType
          .toString()
          .toUpperCase()}`,
        state,
      ),
    {
      id: accountId,
      currentContactId: contactId,
      relationshipType: contactRelationshipType,
    },
  );

export const validate = (
  accountId: string,
  contactId: string,
  contactRelationshipType: string,
  state: State,
) =>
  fetchWithParamValidation(
    ({ id, currentContactId, relationshipType }) =>
      postToHamsClient(
        `${id}/validateCurrencyWithContactAsMain/${currentContactId}/${relationshipType}`,
        state,
      ),
    {
      id: accountId,
      currentContactId: contactId,
      relationshipType: contactRelationshipType,
    },
  );

export const getPricingPlanAccountOverview = (accountId: string, state: State) =>
  fetchWithParamValidation(({ id }) => getFromHamsClient(`/${id}/overview`, state), {
    id: accountId,
  });

export const cancelSubscription = (accountId: string, state: State) =>
  fetchWithParamValidation(({ id }) => deleteFromHamsClient(`/${id}/autoRenew`, state), {
    id: accountId,
  });

export const sendChurnSurvey = (
  accountId: string,
  message: string,
  messagekey: string,
  comment: string,
  environment: string,
  state: State,
) =>
  fetchWithParamValidation(
    ({ id }) =>
      postToHamsClient(
        `/${id}/churnSurvey`,
        state,
        {},
        {
          message,
          messagekey,
          comment,
          source: 'mac',
          environment,
        },
      ),
    {
      id: accountId,
    },
  );

export const getCreditCardUpdatableAccounts = (state: State) =>
  fetchWithParamValidation(() => getFromHamsClient(`/creditCardUpdatableAccounts`, state), {});

export const getRenewalTotalForDisplay = (
  accountId: string,
  shouldCalculateTax: boolean,
  displayCents: boolean,
  displayTaxType: boolean,
  displayPaymentPeriod: boolean,
  state: State,
) =>
  fetchWithParamValidation(
    ({ id, calculateTax, cents, taxType, paymentPeriod }) =>
      getFromHamsClient(
        `/${id}/renewalTotalForDisplay?shouldCalculateTax=${calculateTax}&displayCents=${cents}&displayTaxType=${taxType}&displayPaymentPeriod=${paymentPeriod}`,
        state,
      ),
    {
      id: accountId,
      calculateTax: shouldCalculateTax,
      cents: displayCents,
      taxType: displayTaxType,
      paymentPeriod: displayPaymentPeriod,
    },
  );

export const updateServerId = (accountId: string, serverId: string, state: State) =>
  fetchWithParamValidation(
    ({ id, sid }) => postToHamsClientWithResponseHandlers(`/${id}/sid/${sid}`, state),
    {
      id: accountId,
      sid: serverId,
    },
  );

export const isHostedAccountProvisioning = async (accountId: number, state: State) =>
  fetchWithParamValidation(
    ({ id }) => getFromHamsClient(`/${id}/isHostedAccountProvisioning`, state),
    {
      id: accountId,
    },
  );

export const isSuspended = async (accountId: number, state: State) =>
  fetchWithParamValidation(({ id }) => getFromHamsClient(`/${id}/suspended`, state), {
    id: accountId,
  });

export const postActivate = (accountId: number, state: State) =>
  fetchWithParamValidation(({ id }) => postToHamsClient(`/${id}/activate`, state), {
    id: accountId,
  });

export const postCreditCardDetails = (accountId: number, state: State) => {
  const creditCardData = MACCreditCardDetails().load();

  return fetchWithParamValidation(
    ({ id }) => postToHamsClient(`/${id}/creditCardDetails`, state, {}, creditCardData),
    {
      id: accountId,
    },
  );
};

export const postUpdateRenewalCurrencyOnReconcilableValidationResultAccounts = (
  validateCountry: any,
  state: State,
) =>
  fetchWithParamValidation(
    () =>
      postToHamsClient(
        `/updateRenewalCurrencyOnReconcilableValidationResultAccounts?reason=${encodeURI(
          'Customer updated contact details through MAC',
        )}`,
        state,
        {},
        validateCountry,
      ),
    {},
  );
export const createPersistedLicense = async (
  licenseDetails: CreateLicenseDetails,
  state: State,
) => {
  return fetchWithParamValidation(
    () => postToHamsClient(`/persistedLicense`, state, {}, licenseDetails),
    {},
  );
};
